import * as React from 'react';
import { DocAccessType, IDocuments, IDocumentsList } from '../../../../types';
import { Section } from '../../../Finger/Section';
import FingerWithApprove from '../../../atoms/finger-with-approve';
import NoDocuments from '../../../atoms/no-doc-placeholder';
import { getFingerStatus, getVisible, hasDeleted } from '../../../../utilities/documents';
import { UserRole } from '../../../../store/auth/types';

interface IProps {
  infoOnly: boolean;
  userAccess?: DocAccessType;
  documents: IDocuments;
  courseName: string;
  courseYear: string;
  warning?: string;
  userRole?: UserRole;
  canResume: boolean;
  canUpload: boolean;
  canOpen: boolean;
  canApprove: boolean;
  canReject: boolean;
  isDeleting: boolean;
  canAcknowledge: boolean;
  onResume: (url: string) => void;
  onAcademicTranscriptUploadClick: (docKey: string) => void;
  onAcademicTranscriptOpenClick: (docKey: string) => void;
  onAcademicTranscriptApproveClick: (docKey: string) => void;
  onAcademicTranscriptRejectClick: (docKey: string) => void;
  onAcademicTranscriptRescindRejectClick: (docKey: string) => void;
  onAcademicTranscriptDeleteClick: (docKey: string) => void;
  onAcademicTranscriptAcknowledgeClick: (docKey: string) => void;
  onAcademicCertificateUploadClick: (docKey: string) => void;
  onAcademicCertificateOpenClick: (docKey: string) => void;
  onAcademicCertificateApproveClick: (docKey: string) => void;
  onAcademicCertificateRejectClick: (docKey: string) => void;
  onAcademicCertificateRescindRejectClick: (docKey: string) => void;
  onAcademicCertificateDeleteClick: (docKey: string) => void;
  onAcademicCertificateAcknowledgeClick: (docKey: string) => void;
}

const PreviousStudy = (props: IProps) => {
  const { academicCertificate } = props.documents;
  const { academicTranscript } = props.documents;

  const { courseName, courseYear, warning, infoOnly, userAccess, userRole } = props;
  let hasDocs = false;
  let hasDeletedDocs = false;
  const docNodes: React.ReactNode[] = [];

  if (academicTranscript && Object.keys(academicTranscript).length > 0) {
    const visibleDocs = getVisible({
      documentsHash: academicTranscript,
      userAccess,
      infoOnly,
      userRole,
    }) as IDocumentsList[];
    if (hasDeleted(academicTranscript)) {
      hasDeletedDocs = true;
    }
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((AT: IDocumentsList, index: number) => {
          const { key, item } = AT;
          return (
            <FingerWithApprove
              key={`academic-transcript-${index}`}
              status={getFingerStatus(item)}
              isProcessingPdf={item.isProcessingPdf}
              reason={item.reason}
              label={`Academic transcript ${
                Object.keys(academicTranscript).length > 1 ? (index > 0 ? index + 1 : '') : ''
              }`}
              fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
              fileNames={item.fileNames}
              canUpload={infoOnly ? false : props.canUpload}
              canOpen={infoOnly ? false : props.canOpen}
              canApprove={infoOnly ? false : props.canApprove}
              canReject={infoOnly ? false : props.canReject}
              isDeleting={infoOnly ? false : props.isDeleting}
              canAcknowledge={infoOnly ? props.canAcknowledge : false}
              onOpenClick={() => props.onAcademicTranscriptOpenClick(key)}
              onUploadClick={() => props.onAcademicTranscriptUploadClick(key)}
              onApproveClick={() => props.onAcademicTranscriptApproveClick(key)}
              onRejectClick={() => props.onAcademicTranscriptRejectClick(key)}
              onRescindRejectClick={() => props.onAcademicTranscriptRescindRejectClick(key)}
              onDeleteClick={() => props.onAcademicTranscriptDeleteClick(key)}
              onAcknowledgeClick={() => props.onAcademicTranscriptAcknowledgeClick(key)}
              userRole={props.userRole}
              deletedByTTL={item.deletedByTTL}
            />
          );
        }),
      );
    }
  }

  if (academicCertificate && Object.keys(academicCertificate).length > 0) {
    const visibleDocs = getVisible({
      documentsHash: academicCertificate,
      userAccess,
      infoOnly,
      userRole,
    }) as IDocumentsList[];
    if (hasDeleted(academicTranscript)) {
      hasDeletedDocs = true;
    }
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((AC: IDocumentsList, index: number) => {
          const { key, item } = AC;
          return (
            <FingerWithApprove
              key={`academic-certificate-${index}`}
              status={getFingerStatus(item)}
              isProcessingPdf={item.isProcessingPdf}
              reason={item.reason}
              fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
              label={`Academic certificate ${
                Object.keys(academicCertificate).length > 1 ? (index > 0 ? index + 1 : '') : ''
              }`}
              fileNames={item.fileNames}
              canUpload={infoOnly ? false : props.canUpload}
              canOpen={infoOnly ? false : props.canOpen}
              canApprove={infoOnly ? false : props.canApprove}
              canReject={infoOnly ? false : props.canReject}
              isDeleting={infoOnly ? false : props.isDeleting}
              canAcknowledge={infoOnly ? props.canAcknowledge : false}
              onUploadClick={() => props.onAcademicCertificateUploadClick(key)}
              onOpenClick={() => props.onAcademicCertificateOpenClick(key)}
              onApproveClick={() => props.onAcademicCertificateApproveClick(key)}
              onRejectClick={() => props.onAcademicCertificateRejectClick(key)}
              onRescindRejectClick={() => props.onAcademicCertificateRescindRejectClick(key)}
              onDeleteClick={() => props.onAcademicCertificateDeleteClick(key)}
              onAcknowledgeClick={() => props.onAcademicCertificateAcknowledgeClick(key)}
              userRole={props.userRole}
              deletedByTTL={item.deletedByTTL}
            />
          );
        }),
      );
    }
  }

  if (!hasDocs && !hasDeletedDocs) {
    return null;
  }

  if ((infoOnly || userAccess) && !hasDocs) {
    return null;
  }

  return (
    <Section
      id="previous-study"
      label={`Upload your certificate and transcript for your ${courseName} from ${courseYear}`}
      canEdit={props.canResume}
      edit={() => {
        props.onResume('history/step-1');
      }}
      warning={warning}
    >
      {hasDocs ? docNodes : <NoDocuments />}
    </Section>
  );
};

export default PreviousStudy;
